import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text } from '@chakra-ui/react'

import { Item } from 'appTypes/mapper'
import { ProductCardSign, ProductCardSignProps } from 'components/ui/ProductCardSign'
import { removeDecimals, round } from 'utils/removeDecimals'
import { trimStringToLength } from 'utils/trimStringToLength'

import { CardSummary, ImageWrapper, StyledCard } from './styles'

type Props = {
  product?: Item
  onSignClick?: () => void
  shouldDisplaySign?: boolean
  shouldDisplayAvailableInfo?: boolean
}

export const ProductCard: React.FC<Props & ProductCardSignProps> = ({
  sign,
  isChecked = false,
  product,
  onSignClick,
  shouldDisplaySign = false,
  shouldDisplayAvailableInfo = true,
}) => {
  const { t } = useTranslation('common')

  const sortedImages =
    product?.products[0] && [...(product?.products[0].images ?? [])].sort((a, b): number => a.id - b.id)

  const getAvailableExtraLocations = (product: Item): string => {
    if (!product.availableAtLocations || product.availableAtLocations.locations.length <= 1) {
      return ''
    }

    return `+ ${product.availableAtLocations.locations.length - 1} ${t('more')}`
  }

  const getManufaturerInfo = (manufacturer: string, productModel: string): string => {
    const parts: string[] = []
    if (manufacturer) {
      parts.push(manufacturer)
    }

    if (productModel) {
      parts.push(productModel)
    }

    return parts.join(' - ')
  }

  return (
    <StyledCard onClick={onSignClick}>
      {shouldDisplaySign && (
        <Box position="absolute" right="4px" top="4px">
          <ProductCardSign isChecked={isChecked} sign={sign} />
        </Box>
      )}
      <ImageWrapper>
        <img loading="lazy" src={(sortedImages && sortedImages[0] && sortedImages[0].url) || ''} alt="" />
      </ImageWrapper>
      <CardSummary isBigger={shouldDisplayAvailableInfo}>
        <Text fontSize="sm" fontWeight="bold" lineHeight="1.2" wordBreak="break-all">
          {trimStringToLength(product?.name ?? '', 40)}
        </Text>

        {(product?.manufacturer || product?.productModel) && (
          <Text fontSize="sm" wordBreak="break-all">
            {getManufaturerInfo(product?.manufacturer ?? '', product?.productModel ?? '')}
          </Text>
        )}

        {product && product.closestLocation && (
          <Text fontSize="xs">
            {t('closestLocation')}: {product.closestLocation} {product.distance && `(${round(product.distance, 2)} km)`}{' '}
            {getAvailableExtraLocations(product)}
          </Text>
        )}

        {product?.minimalDailyPrice && (
          <Text fontSize="xs">
            {t('from')} {removeDecimals(product.minimalDailyPrice)} {t('nokPerDay')}
          </Text>
        )}
      </CardSummary>
    </StyledCard>
  )
}
